// Everything the UI needs to answer "can this visitor install the app, and how?"
//
// Three separate install routes exist and they are not interchangeable:
//
//   · The APK          — Android browsers only. Sideloaded, so it needs the
//                        "install unknown apps" permission and trips a Chrome
//                        warning. Gives the real packaged app.
//   · The PWA          — Android + desktop through beforeinstallprompt; iOS
//                        only through Safari's manual Share > Add to Home
//                        Screen, because WebKit does not fire that event.
//   · Nothing to do    — already installed, or already inside the packaged app.
//
// The page and the banner both need this same read, so it lives here rather
// than being derived twice with two subtly different answers.
import { useState, useEffect, useCallback } from 'react';
import { isNativePlatform } from './nativeBridge';

// True once the page is running as an installed app rather than a browser tab.
// display-mode: standalone covers Android/desktop PWAs; navigator.standalone is
// the iOS-only equivalent Safari never replaced.
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.navigator?.standalone === true;
}

export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports itself as a Mac, and the only reliable tell left is that
  // it has a touch screen. Without this an iPad is offered the APK, which it
  // can never install.
  const iOSLike = /iPad|iPhone|iPod/.test(ua)
    || (/Macintosh/.test(ua) && typeof document !== 'undefined' && navigator.maxTouchPoints > 1);
  if (iOSLike) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

export default function usePwaInstall() {
  // The deferred beforeinstallprompt event. Chrome fires this once, and the
  // event object is the ONLY handle on the install dialog -- it cannot be
  // reconstructed later, which is why it is captured into state rather than
  // read on demand.
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [platform] = useState(() => detectPlatform());
  const inNativeApp = isNativePlatform();

  useEffect(() => {
    if (inNativeApp) return undefined;

    const onBeforeInstall = (e) => {
      // Chrome shows its own mini-infobar unless the event is prevented. We
      // prevent it so the install offer appears where we decide (the banner and
      // the /app page) rather than in a strip the user has learned to ignore.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // The event may already have fired before this component mounted (it is
    // dispatched early on a warm load). matchMedia is the fallback signal that
    // the app got installed in another tab meanwhile.
    const mq = window.matchMedia?.('(display-mode: standalone)');
    const onModeChange = (e) => setInstalled(e.matches);
    mq?.addEventListener?.('change', onModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      mq?.removeEventListener?.('change', onModeChange);
    };
  }, [inNativeApp]);

  // Opens the browser's own install dialog. Resolves to true only if the user
  // actually accepted -- Chrome resolves userChoice either way, and a dismissed
  // prompt must not be reported as a success.
  //
  // The deferred event is single-use: once prompt() has been called the browser
  // will not honour it again, so it is cleared regardless of the outcome.
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return outcome === 'accepted';
    } catch {
      setDeferredPrompt(null);
      return false;
    }
  }, [deferredPrompt]);

  return {
    platform,
    installed,
    inNativeApp,
    // Whether the one-tap browser install is available right now. False on iOS
    // always, and false on Android once the app is installed or the event has
    // been spent.
    canPromptInstall: Boolean(deferredPrompt) && !installed && !inNativeApp,
    promptInstall,
    // iOS gets instructions instead of a button, because Safari offers no API.
    needsManualIosInstall: platform === 'ios' && !installed && !inNativeApp,
    canDownloadApk: platform === 'android' && !inNativeApp,
  };
}
